import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractContentService } from '../../abstracts/services/common/content.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IPageContent } from '../../interfaces/common/content/page-content.interface';
import { IClientAdditionalInfoRequestPageContent } from '../../interfaces/onboarding/dashboard/client-additional-info-request-page-content.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import { applyEnrollmentIdToQueryParams } from '../../ui-helpers/functions/user-context-url.func';

@Injectable({
  providedIn: 'root',
})
export class ContentService extends AbstractContentService {
  private apiUrl = environment.apiUrl;
  private navigationControllerSuffix = 'Onboarding/Onboarding/navigation/';
  constructor(
    private http: HttpClient,
    private store: Store<IAppState>,
  ) {
    super();
  }

  /**
   * Get Page Content by ID
   */
  protected async getPageContentById(contentId: number): Promise<IPageContent> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-content/${contentId}`;
    const result = await firstValueFrom(
      this.http
        .get<IApiResponse>(url)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    return result.data as IPageContent;
  }

  async getPageContentByConstId(idConst: number): Promise<string> {
    return (await this.getPageContentById(idConst)).displayText;
  }

  async getContentDictByStateId(
    stateId: string,
  ): Promise<Map<number, IPageContent>> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-content/state`;
    const body = {
      id: stateId,
    };
    const result = await firstValueFrom(
      this.http
        .post<IApiResponse>(url, body)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    const contentMap = new Map();
    (result.data as IPageContent[]).forEach((data) => {
      contentMap.set(data.enumRef, data);
    });
    return contentMap;
  }

  async getContentDictByStateId_ClientPersonalized(
    stateId: string,
  ): Promise<Map<number, IPageContent>> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-content/clientstate`;
    const body = {
      id: stateId,
    };
    const result = await firstValueFrom(
      this.http
        .post<IApiResponse>(url, body, { params })
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    const contentMap = new Map();
    (result.data as IPageContent[]).forEach((data) => {
      contentMap.set(data.enumRef, data);
    });
    return contentMap;
  }

  async getContentDictByStateId_CustomerPersonalized(
    stateId: string,
    clientCustomerId: number,
  ): Promise<Map<number, IPageContent>> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-content/customerstate`;
    const body = {
      stateId,
      customerId: clientCustomerId,
    };
    const result = await firstValueFrom(
      this.http
        .post<IApiResponse>(url, body)
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    const contentMap = new Map();
    (result.data as IPageContent[]).forEach((data) => {
      contentMap.set(data.enumRef, data);
    });
    return contentMap;
  }

  async getContentDictByStateId_ClientAdditionalInfoRequestPageContent(
    stateId: string,
  ): Promise<Map<number, IClientAdditionalInfoRequestPageContent>> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-content/client-additional-info-request-dashboard`;
    const body = {
      id: stateId,
    };
    const result = await firstValueFrom(
      this.http
        .post<IApiResponse>(url, body, { params })
        .pipe(
          catchError((error: ErrorEvent) =>
            throwError(() => new Error(error.message)),
          ),
        ),
    );
    console.log('ClientAdditionalInfoReq personalized result, ', result);
    const contentMap = new Map();
    (result.data as IClientAdditionalInfoRequestPageContent[]).forEach(
      (data) => {
        contentMap.set(data.pageContentModel.enumRef, data);
      },
    );
    return contentMap;
  }
}
